import {
  pointAtDistance,
  sweptClosestPoint,
  type RopeClosestPoint,
  type RopePathPoint,
  type RopePoint,
} from "./ropeGeometry";
import { CATCH, ROPE, SIM_DT } from "./config";

/**
 * Headless, deterministic Verlet rope simulation.
 *
 * This is the authoritative physics for a vine: client and server run the
 * exact same integration. There is no PixiJS, no real clock, no Math.random —
 * the only randomness is the wind, whose phase is a deterministic `windSeed`
 * supplied by the round's level RNG. Time advances strictly in `SIM_DT`
 * steps (`tick`), so the wind term is reproducible.
 *
 * Extracted from the client `Rope`; mesh/texture/visual offsets stay client-side.
 */
export class RopeSim {
  readonly points: RopePoint[] = [];
  /** Point positions at the START of the current tick (render interpolation). */
  readonly prevPoints: { x: number; y: number }[] = [];
  readonly segmentLen: number;
  readonly segmentCount: number;

  private pivotX: number;
  private pivotY: number;
  private topPinned = true;
  private gripPin:
    | { index: number; x: number; y: number; blend: number; releasing: boolean }
    | null = null;

  private readonly windSeed: number;
  /** Monotonic count of SIM_DT steps taken — the deterministic clock. */
  private tick = 0;

  constructor(
    pivotX: number,
    pivotY: number,
    length: number,
    segments: number,
    windSeed: number,
  ) {
    this.pivotX = pivotX;
    this.pivotY = pivotY;
    this.segmentCount = segments;
    this.segmentLen = length / (segments - 1);
    this.windSeed = windSeed;

    for (let i = 0; i < segments; i++) {
      const y = pivotY + i * this.segmentLen;
      this.points.push({ x: pivotX, y, oldX: pivotX, oldY: y });
      this.prevPoints.push({ x: pivotX, y });
    }
  }

  get length(): number {
    return this.segmentLen * (this.segmentCount - 1);
  }

  getTick(): number {
    return this.tick;
  }

  setPivot(x: number, y: number) {
    const dx = x - this.pivotX;
    const dy = y - this.pivotY;
    if (dx === 0 && dy === 0) return;
    this.pivotX = x;
    this.pivotY = y;
    for (const p of this.points) {
      p.x += dx;
      p.y += dy;
      p.oldX += dx;
      p.oldY += dy;
    }
    for (const pp of this.prevPoints) {
      pp.x += dx;
      pp.y += dy;
    }
  }

  setGripPin(distanceAlong: number, x: number, y: number) {
    const index = Math.max(
      0,
      Math.min(this.segmentCount - 1, Math.round(distanceAlong / this.segmentLen)),
    );
    if (!this.gripPin || this.gripPin.index !== index) {
      this.gripPin = { index, x, y, blend: 0, releasing: false };
      return;
    }
    this.gripPin.x = x;
    this.gripPin.y = y;
    this.gripPin.releasing = false;
  }

  releaseGrip() {
    if (this.gripPin) this.gripPin.releasing = true;
  }

  releaseTop() {
    this.topPinned = false;
  }

  tipX(): number {
    return this.points[this.points.length - 1].x;
  }

  pointAtDistance(distanceAlong: number): RopePathPoint {
    return pointAtDistance(this.points, distanceAlong, this.length);
  }

  applyImpulseAt(
    distanceAlong: number,
    vx: number,
    vy: number,
    radius = this.segmentLen * 2,
  ) {
    const hit = this.pointAtDistance(distanceAlong);
    for (const p of this.points) {
      const dx = p.x - hit.x;
      const dy = p.y - hit.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > radius) continue;
      const strength = 1 - dist / radius;
      p.oldX -= vx * strength;
      p.oldY -= vy * strength;
    }
  }

  sweptClosestPointTo(
    a0x: number,
    a0y: number,
    a1x: number,
    a1y: number,
  ): RopeClosestPoint {
    return sweptClosestPoint(this.points, a0x, a0y, a1x, a1y);
  }

  /** Advance exactly one fixed `SIM_DT`. */
  step() {
    const dt = SIM_DT;
    const n = this.points.length;

    // Snapshot pre-step positions for render interpolation.
    for (let i = 0; i < n; i++) {
      this.prevPoints[i].x = this.points[i].x;
      this.prevPoints[i].y = this.points[i].y;
    }

    if (this.topPinned) {
      this.points[0].x = this.pivotX;
      this.points[0].y = this.pivotY;
      this.points[0].oldX = this.pivotX;
      this.points[0].oldY = this.pivotY;
    }

    const damping = ROPE.damping;
    const startI = this.topPinned ? 1 : 0;
    // Wind phase from the deterministic tick clock, not a wall clock.
    const time = this.tick * dt;
    const wind =
      Math.sin(time * ROPE.windFrequency + this.windSeed) * ROPE.windAmplitude * dt;

    for (let i = startI; i < n; i++) {
      const p = this.points[i];
      const vx = (p.x - p.oldX) * damping;
      const vy = (p.y - p.oldY) * damping;
      p.oldX = p.x;
      p.oldY = p.y;
      const windFactor = Math.sin((i / (n - 1)) * Math.PI);
      p.x += vx + wind * windFactor;
      p.y += vy + ROPE.gravity * dt * dt;
    }

    for (let iter = 0; iter < ROPE.constraintIterations; iter++) {
      for (let i = 0; i < n - 1; i++) {
        const a = this.points[i];
        const b = this.points[i + 1];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 0.0001;
        const diff = (dist - this.segmentLen) / dist;
        const ox = dx * 0.5 * diff;
        const oy = dy * 0.5 * diff;
        const aPinned = i === 0 && this.topPinned;
        if (!aPinned) {
          a.x += ox;
          a.y += oy;
          b.x -= ox;
          b.y -= oy;
        } else {
          b.x -= ox * 2;
          b.y -= oy * 2;
        }
      }
      if (this.topPinned) {
        this.points[0].x = this.pivotX;
        this.points[0].y = this.pivotY;
      }
    }

    this.applyGrip(dt);
    this.tick++;
  }

  private applyGrip(dt: number) {
    const grip = this.gripPin;
    if (!grip) return;

    const dir = grip.releasing ? -1 : 1;
    grip.blend = Math.max(0, Math.min(1, grip.blend + (dt / CATCH.gripBlend) * dir));

    const p = this.points[grip.index];
    const k = grip.blend * grip.blend * (3 - 2 * grip.blend);
    p.x += (grip.x - p.x) * k;
    p.y += (grip.y - p.y) * k;
    p.oldX += (grip.x - p.oldX) * k;
    p.oldY += (grip.y - p.oldY) * k;

    if (grip.releasing && grip.blend === 0) this.gripPin = null;
  }

  /** Stable digest of point positions — for divergence detection / tests. */
  stateHash(): string {
    let h = 0x811c9dc5;
    const q = (v: number) => Math.round(v * 1000) | 0;
    for (const p of this.points) {
      for (const c of [q(p.x), q(p.y), q(p.oldX), q(p.oldY)]) {
        h ^= c;
        h = Math.imul(h, 0x01000193);
      }
    }
    return (h >>> 0).toString(16).padStart(8, "0");
  }
}
